import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, NgZone, Output, ViewChild } from '@angular/core';

import { ApiAuthService } from '@services/api-auth.service';
import { GoogleAuthService } from '@services/google-auth.service';
import { CustomerModel } from '@models/CustomerModel';
import { Country } from '@models/Enums';

/**
 * Card "Crear tu cuenta": ofrece Google como opción principal y el registro por
 * correo como alternativa. Encapsula todo el trato con Google Identity Services y
 * el canje del idToken por una sesión de Givi (grant `givi-google`).
 *
 * El API crea el customer si el correo no existía, así que `linked` siempre emite
 * una cuenta con id.
 */
@Component({
    selector: 'app-google-signin-card',
    templateUrl: './google-signin-card.component.html',
    styleUrls: ['./google-signin-card.component.scss']
})
export class GoogleSigninCardComponent implements AfterViewChecked {

    @Input() title = 'Crear tu cuenta';
    @Input() emailLabel = 'Registrarme con correo';

    /** Solo el botón de Google, sin título ni la opción de correo. */
    @Input() buttonOnly = false;

    /** Texto del botón, para adaptarlo al contexto donde se use. */
    @Input() buttonLabel = 'Registrarme con Google';

    /** País del flujo (viene de la URL del checkout). Va en X-COUNTRY al canjear el idToken. */
    @Input() country: Country = null;

    /** Cuenta vinculada (creada o existente). */
    @Output() linked = new EventEmitter<CustomerModel>();

    /** El usuario prefirió registrarse con correo. */
    @Output() emailSelected = new EventEmitter<void>();

    /** Se avisa para que el padre muestre su propio loader. */
    @Output() loadingChange = new EventEmitter<boolean>();

    @ViewChild('googleBtnHost') googleBtnHost: ElementRef<HTMLDivElement>;

    error: string = null;

    private buttonMounted = false;

    constructor(private apiAuthService: ApiAuthService,
        private googleAuthService: GoogleAuthService,
        private zone: NgZone) {
    }

    // El host aparece con el *ngIf del padre, así que se monta cuando entra al DOM.
    ngAfterViewChecked() {

        this.mountButton();
    }

    onSelectEmail() {

        this.emailSelected.emit();
    }

    /** Olvida la cuenta elegida para que el próximo intento vuelva a preguntar. */
    reset() {

        this.error = null;
        this.buttonMounted = false;
        this.googleAuthService.disableAutoSelect();
    }

    private mountButton() {

        if (!this.googleBtnHost || this.buttonMounted) return;

        this.buttonMounted = true;

        this.googleAuthService.renderButton(
            this.googleBtnHost.nativeElement,
            (idToken: string) => this.onCredential(idToken)
        ).catch((err: any) => {

            this.buttonMounted = false;
            this.error = err?.message || 'No se pudo cargar el acceso con Google.';
        });
    }

    /**
     * GIS invoca el callback fuera de la zona de Angular: sin zone.run() el cambio
     * de estado no dispara detección de cambios y la vista no se actualiza.
     */
    private onCredential(idToken: string) {

        this.zone.run(() => this.exchangeToken(idToken));
    }

    private exchangeToken(idToken: string) {

        const email = this.decodeEmail(idToken);

        if (!email) {

            this.error = 'No se pudo leer la cuenta de Google.';
            return;
        }

        this.error = null;
        this.loadingChange.emit(true);

        this.apiAuthService.loginWithGoogle(email, idToken, this.country).then(
            (session) => {

                this.loadingChange.emit(false);
                this.linked.emit(session.user);

            }).catch((err: any) => {

                this.loadingChange.emit(false);
                this.error = err?.message || 'No se pudo iniciar sesión con Google.';
            });
    }

    /** El idToken es un JWT: el payload trae el email ya verificado por Google. */
    private decodeEmail(idToken: string): string {

        try {

            const payload = idToken.split('.')[1];

            // base64url y sin padding: atob necesita '+', '/' y el relleno de '='
            let normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
            while (normalized.length % 4 !== 0) normalized += '=';

            const json = decodeURIComponent(
                atob(normalized).split('').map(
                    c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                ).join('')
            );

            return JSON.parse(json)?.email || '';

        } catch {

            return '';
        }
    }
}
